import { put, takeLatest, call } from 'redux-saga/effects'
import API from "store/login/loginApis";
import * as types from "store/login/loginTypes";
import auth from 'utils/auth'
import Storage from 'shared/helpers/storage'
import { parseJwt } from 'shared/helpers/functional'

// QUAL-2201: never log an axios response or error object here. axios attaches the request config
// to both - config.data is the login/reset password, config.headers carries the bearer token - and
// the login response body holds the access token. Named, non-secret fields only.
const responseLogFields = response => ({ status: response && response.status })
const errorLogFields = err => ({
  status: err && err.response && err.response.status,
  code: err && err.code,
  noResponse: !(err && err.response)
})

export function* logout () {
  try {
    auth.signOut()
    yield put({ type: 'LOGOUT_SUCCEEDED' })
    Storage.clear()
  } catch (error) {
    yield put({
      type: 'ASYNC_FETCH_FAILED',
      nextActionType: 'LOGIN_FAILED',
      error,
    })
  }
}

export function* loginRequest({payload : payload}) {
  try {
    Storage.clear()
    const response =  yield call(API.loginRequest,payload);
    if (response.status === 203 ) {
      console.log("login requires terms acceptance", responseLogFields(response))
      const terms = response.data.termsAcceptedRes.body.terms
      yield put({type: 'POST_LOGIN_TERMS_NOT_ACCEPTED', terms: terms})
    }
    else if ( response.status === 200 ) {
      const id = response.data.body.userInfo.id 
      const accessToken =  response.data.body.accessToken
      Storage.setItem('accessToken', accessToken)
      Storage.setItem('x-api-key',response.data.apiKey)
      const tokenDecoded = parseJwt(accessToken)
      yield put({type:'POST_LOGIN_REQUEST_SUCCESSED', payload: response.data, roles: tokenDecoded['cognito:groups'], accountsSelected: response.data.body.userInfo.accountsSelected});
      console.log("login succeeded", responseLogFields(response))
      yield put({type:'FETCH_USER_SUCCEEDED', payload: response.data.body.userInfo})
    } else {
      yield put({type:'POST_LOGIN_REQUEST_FAILED'})
    }
  } catch (err) {
      console.log("Login request failed: ", errorLogFields(err));
      Storage.clear()
      yield put({type:'POST_LOGIN_REQUEST_FAILED', message: err.message})
    }
}

function* adminLoginRequest(resp) {
  try {
    Storage.clear()
    if ( resp.payload.statusCode === 200 ) {
      const idTokenDecoded = parseJwt(resp.payload.body.id_token)
      Storage.setItem('accessToken', resp.payload.body.id_token)
      Storage.setItem('x-api-key', process.env.REACT_APP_X_API_KEY)
      Storage.setItem('isIncrowdAdminLogin', true)
      yield put({type:'POST_ADMIN_LOGIN_REQUEST_SUCCEEDED', payload: resp.payload, roles: idTokenDecoded['cognito:groups'], accountsSelected: ""});
    } else {
      yield put({type:'POST_LOGIN_REQUEST_FAILED'})
    }
  } catch (err) {
      console.log("Admin Login request failed: ",err);
      Storage.clear()
      yield put({type:'POST_LOGIN_REQUEST_FAILED', message: err.message})
    }
}

export function* updatePasswordRequest({ payload: payload }) {
  try {
    const response = yield call(API.updatePasswordRequest, payload);
    if (response.status === 200) {
      yield put({ type: 'POST_UPDATE_PASSWORD_SUCCESSED', status:response.status });
      if(payload.isForgetPassword){
        yield put({ type: 'CLOSE_RESET_PASSWORD_MODAL'});
        window.location.replace(window.location.origin + "/login")
      } 
    } else if (response.status === 400 || response.status === 403 || response.status === 401) {
      yield put({ type: 'POST_UPDATE_PASSWORD_FAILED', status:response.status})
    } else {
      yield put({ type: 'POST_UPDATE_PASSWORD_FAILED', status:response.status })
      yield put({ type: 'SHOW_MESSAGE', payload: { message: "Something went wrong please try again later",className: 'copy-screeners-snackbar'}})
      yield put({ type: 'HIDE_MESSAGE', payload: { className: 'copy-screeners-snackbar'}})
    }
  } catch (err) {
    // QUAL-2201: with no response at all (network error, or a gateway timeout whose error reply
    // carries no CORS headers) err.response is undefined; reading .status threw inside this catch,
    // killed the saga before POST_UPDATE_PASSWORD_FAILED and left the reset spinner running forever.
    const status = err.response && err.response.status
    if (status === 401) {
      yield put({ type: 'POST_UPDATE_PASSWORD_FAILED', status })
    } else {
    // err carries the axios request config, i.e. the new password and reset token - named fields only.
    console.log("update password request failed: ", errorLogFields(err));
    yield put({ type: 'POST_UPDATE_PASSWORD_FAILED', message: err.message })
    yield put({ type: 'SHOW_MESSAGE', payload: { message: "There was a problem processing your request",className: 'copy-screeners-snackbar'}})
    yield put({ type: 'HIDE_MESSAGE', payload: { className: 'copy-screeners-snackbar'}})
    }
  }
}


export function* updateAccountSelection ({ payload }) {
  console.log("payload in update account saga ", payload)
  try {
    const response = yield call(API.updateAccountSelection, payload)
    console.log("response in updateAccountSelection ", responseLogFields(response))
    if(response.status === 200) {
      yield put({type: 'UPDATE_ACCOUNT_SELECTION_SUCCEEDED', payload: response})
    }
  } catch (err) {
    console.log("error in getting clients for updated account ", errorLogFields(err))
    yield put({ type: 'SHOW_MESSAGE', payload: { message: "There was a problem processing your request",className: 'copy-screeners-snackbar'}})
    yield put({ type: 'HIDE_MESSAGE', payload: { className: 'copy-screeners-snackbar'}})
  }
}

// QUAL-2135: watchIsI2andQsToolUser removed. It called check-qsTool-i2 and branched three
// ways on the reply - open the InCrowd confirmation modal, send the reset mail straight
// away, or display "We can't find a user with that email address". Those three visible
// outcomes were the enumeration oracle: they told anyone who typed an address whether it
// belonged to an account, and which products that account had. The screen now opens the
// confirmation modal for every address and sends the mail on confirm, so all three collapse
// into one.

export function* watchSendResetPasswordEmailRequest({ payload: payload }) {
  try {
    const response = yield call(API.sendResetPasswordEmailRequest, payload);
    if (response.status === 200) {
      yield put({ type: 'SEND_RESET_PASSWORD_EMAIL_SUCCESSED' });
    } else {
      yield put({ type: 'SEND_RESET_PASSWORD_EMAIL_FAILED' })
      yield put({ type: 'SHOW_MESSAGE', payload: { message: "Something went wrong please try again later", className: 'copy-screeners-snackbar' } })
      yield put({ type: 'HIDE_MESSAGE', payload: { className: 'copy-screeners-snackbar' } })
    }
    if (payload.closeModal) {
      yield put({ type: 'TOGGLE_I2_AND_QS_TOOL_USER_MODAL' })
    }
  } catch (err) {
    console.log("reset password request failed: ", errorLogFields(err));
    yield put({ type: 'SEND_RESET_PASSWORD_EMAIL_FAILED', message: err.message })
    yield put({ type: 'SHOW_MESSAGE', payload: { message: "There was a problem processing your request", className: 'copy-screeners-snackbar' } })
    yield put({ type: 'HIDE_MESSAGE', payload: { className: 'copy-screeners-snackbar' } })
  }
}


export default function* watchLogin () {
  yield takeLatest('LOGOUT', logout)
  yield takeLatest(types.POST_LOGIN_REQUESTED, loginRequest)
  yield takeLatest(types.POST_ADMIN_LOGIN_REQUESTED, adminLoginRequest)
  yield takeLatest(types.POST_UPDATE_PASSWORD_REQUESTED, updatePasswordRequest)
  yield takeLatest(types.UPDATE_ACCOUNT_SELECTION, updateAccountSelection)
  yield takeLatest(types.SEND_RESET_PASSWORD_EMAIL_REQUESTED, watchSendResetPasswordEmailRequest)
}
